import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Banknote, Loader2, Tag, X, Gift, Phone, User, MapPin, Building2, Truck, Mail } from 'lucide-react';
import { formatPrice, cn } from '../lib/utils';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { resolveImageUrl } from '../lib/imageUrl';
import { toast } from 'sonner';
import { effectiveUnitPrice } from '../utils/pricing';
import { trackCheckoutStart, trackPurchase } from '../lib/tracker';
import { useSiteSettings, applyFreeShipping } from '../lib/siteSettings';
import { computeAffiliateDiscount } from '../lib/promo';

const API_URL = '';
const PHONE_REGEX = /^(0[567]\d{8}|\+213[567]\d{8}|213[567]\d{8})$/;

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cart, clearCart, fetchCart } = useCart();
  const { user } = useAuth();
  const { settings } = useSiteSettings();

  const [wilayas, setWilayas] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [fees, setFees] = useState({ tarif_domicile: 0, tarif_bureau: 0 });
  const [loadingCommunes, setLoadingCommunes] = useState(false);
  const [loading, setLoading] = useState(false);

  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState(null);

  // Loyalty points state
  const [loyaltyData, setLoyaltyData] = useState(null);
  const [usePoints, setUsePoints] = useState(false);
  const [pointsToUse, setPointsToUse] = useState(0);

  const [formData, setFormData] = useState({
    customer_name: user?.name || '',
    customer_email: user?.email || '',
    customer_phone: '',
    shipping_address: '',
    wilaya: '',
    commune: '',
    delivery_type: 'home',
    notes: '',
    website: '', // honeypot
  });
  const [errors, setErrors] = useState({});

  // Initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${API_URL}/api/ecotrack/wilayas`);
        if (response.ok) {
          const data = await response.json();
          setWilayas(data.wilayas || []);
        }
        if (user) {
          const loyaltyRes = await fetch(`${API_URL}/api/loyalty`, { credentials: 'include' });
          if (loyaltyRes.ok) setLoyaltyData(await loyaltyRes.json());
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
    fetchCart();
  }, [fetchCart, user]);

  // Pre-fill from user
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        customer_name: user.name || prev.customer_name,
        customer_email: user.email || prev.customer_email,
        customer_phone: user.phone || prev.customer_phone,
        shipping_address: user.address || prev.shipping_address,
        wilaya: user.wilaya || prev.wilaya,
      }));
    }
  }, [user]);

  // Load communes + fees when wilaya changes
  useEffect(() => {
    if (!formData.wilaya) {
      setCommunes([]);
      setFees({ tarif_domicile: 0, tarif_bureau: 0 });
      return;
    }
    setLoadingCommunes(true);
    Promise.all([
      fetch(`${API_URL}/api/ecotrack/communes/${formData.wilaya}`).then(r => r.json()),
      fetch(`${API_URL}/api/ecotrack/fees/${formData.wilaya}`).then(r => r.json()),
    ])
      .then(([communesData, feesData]) => {
        setCommunes(communesData.communes || []);
        setFees(feesData || { tarif_domicile: 0, tarif_bureau: 0 });
      })
      .catch(() => {})
      .finally(() => setLoadingCommunes(false));
  }, [formData.wilaya]);

  // Meta: InitiateCheckout (once per cart)
  useEffect(() => {
    if (cart.items.length === 0) return;
    const fireKey = 'meta_initiate_checkout_fired';
    if (sessionStorage.getItem(fireKey)) return;
    trackCheckoutStart(
      cart.total,
      cart.items.length,
      cart.items.map(i => i.product_id)
    );
    sessionStorage.setItem(fireKey, '1');
  }, [cart.items.length, cart.total]);

  // Auto-apply affiliate/promo code once cart loads (from ?ref= or localStorage)
  useEffect(() => {
    const urlRef = searchParams.get('ref');
    const storedRef = localStorage.getItem('bloom_affiliate_ref');
    const refCode = urlRef || storedRef;
    if (!refCode || !cart?.items?.length || appliedPromo) return;
    (async () => {
      const codeUpper = refCode.trim().toUpperCase();
      try {
        // Try affiliate first (referral link scenario)
        const affRes = await fetch(`${API_URL}/api/affiliate/validate/${encodeURIComponent(codeUpper)}`);
        if (affRes.ok) {
          const data = await affRes.json();
          const discount = computeAffiliateDiscount(data, cart.total);
          setPromoCode(codeUpper);
          setAppliedPromo({ ...data, is_affiliate: true, code: codeUpper, discount_amount: discount });
          toast.success(`Code ${codeUpper} appliqué automatiquement! ${data.message || ''}`);
          return;
        }
        // Fallback: try as a regular promo code
        const promoItems = cart.items.map((item) => ({
          product_id: item.product_id,
          name: item.product?.name || '',
          price: effectiveUnitPrice(item.product, null),
          quantity: item.quantity,
        }));
        const promoRes = await fetch(`${API_URL}/api/promo/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ code: codeUpper, subtotal: cart.total, items: promoItems }),
        });
        if (promoRes.ok) {
          const data = await promoRes.json();
          setPromoCode(codeUpper);
          setAppliedPromo(data);
          toast.success(`Code ${codeUpper} appliqué automatiquement!`);
        } else {
          localStorage.removeItem('bloom_affiliate_ref');
        }
      } catch (err) {
        console.error('Auto-apply code failed:', err);
      }
    })();
  }, [cart?.items?.length]);

  const selectedWilaya = wilayas.find(
    w => String(w.wilaya_id) === String(formData.wilaya) || w.wilaya_name === formData.wilaya
  );
  const selectedCommune = communes.find(c => c.nom === formData.commune);
  const hasBureauForCommune = selectedCommune ? selectedCommune.has_stop_desk === 1 : false;

  const shippingCost = formData.delivery_type === 'bureau' ? (fees.tarif_bureau || 0) : (fees.tarif_domicile || 0);
  const subtotal = cart.total;
  const finalShipping = applyFreeShipping(shippingCost, subtotal, settings);
  const isFreeShipping = finalShipping === 0 && shippingCost > 0 && subtotal > 0;
  const shippingReady = !!selectedWilaya;

  const freeShippingEnabled = settings.free_shipping_enabled !== false;
  const freeShippingThreshold = Number(settings.free_shipping_threshold ?? 10000);

  const promoDiscount = appliedPromo?.discount_amount || 0;
  const pointsDiscount = usePoints && pointsToUse > 0 ? pointsToUse * (loyaltyData?.points_value_da || 10) : 0;
  const totalDiscount = promoDiscount + pointsDiscount;
  const total = Math.max(0, subtotal + finalShipping - totalDiscount);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: null }));
  };

  const handleApplyPromo = async () => {
    const code = promoCode.trim().toUpperCase();
    if (!code) return;
    setPromoLoading(true);
    try {
      // Try affiliate code first — a code created as an affiliate handle in admin lives in `affiliates`, not `promo_codes`.
      const affRes = await fetch(`${API_URL}/api/affiliate/validate/${encodeURIComponent(code)}`);
      if (affRes.ok) {
        const data = await affRes.json();
        const discount = computeAffiliateDiscount(data, subtotal);
        setAppliedPromo({ ...data, is_affiliate: true, code, discount_amount: discount });
        toast.success(data.message || `Code ${code} appliqué`);
        return;
      }
      // Fallback: regular promo code path
      const promoItems = cart.items.map(item => ({
        product_id: item.product_id,
        name: item.product?.name || '',
        price: effectiveUnitPrice(item.product, null),
        quantity: item.quantity,
      }));
      const response = await fetch(`${API_URL}/api/promo/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code, subtotal, items: promoItems }),
      });
      if (response.ok) {
        const data = await response.json();
        setAppliedPromo(data);
        toast.success(data.message || 'Code appliqué');
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || 'Code invalide');
        setAppliedPromo(null);
      }
    } catch {
      toast.error('Erreur lors de la validation');
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoCode('');
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.customer_name.trim() || formData.customer_name.trim().length < 2) {
      newErrors.customer_name = 'الإسم مطلوب';
    }
    const phone = formData.customer_phone.replace(/\s|-/g, '');
    if (!PHONE_REGEX.test(phone)) newErrors.customer_phone = 'رقم الهاتف غير صحيح';
    if (formData.customer_email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customer_email.trim())) {
      newErrors.customer_email = 'بريد إلكتروني غير صالح';
    }
    if (!formData.wilaya) newErrors.wilaya = 'اختر ولايتك';
    if (!formData.commune) newErrors.commune = 'اختر البلدية';
    if (formData.delivery_type === 'home' && !formData.shipping_address.trim()) {
      newErrors.shipping_address = 'العنوان مطلوب للتوصيل للمنزل';
    }
    if (formData.delivery_type === 'bureau' && !hasBureauForCommune) {
      newErrors.delivery_type = 'لا يوجد مكتب توصيل في هذه البلدية';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error('يرجى تصحيح الحقول المطلوبة');
      return;
    }
    if (cart.items.length === 0) {
      toast.error('السلة فارغة');
      return;
    }
    setLoading(true);
    try {
      const orderItems = cart.items.map(item => ({
        product_id: item.product_id,
        name: item.product.name,
        price: item.product.sale_price || item.product.price,
        size: item.size,
        color: item.color || '',
        quantity: item.quantity,
        image_url: item.product.images?.[0]?.url || null,
      }));
      const orderResponse = await fetch(`${API_URL}/api/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          customer_phone: formData.customer_phone.replace(/\s|-/g, ''),
          items: orderItems,
          payment_method: 'cod',
          promo_code: appliedPromo?.code || null,
        }),
      });
      if (!orderResponse.ok) {
        const err = await orderResponse.json().catch(() => ({}));
        throw new Error(err.detail || 'حدث خطأ أثناء إنشاء الطلب');
      }
      const order = await orderResponse.json();
      trackPurchase(
        order.order_id,
        order.total,
        order.wilaya,
        order.delivery_type,
        (order.items || []).map(it => ({ id: it.product_id, quantity: it.quantity, item_price: it.price })),
      );
      await clearCart();
      navigate(`/order-success?order_id=${order.order_id}`);
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.message || 'حدث خطأ أثناء إنشاء الطلب');
    } finally {
      setLoading(false);
    }
  };

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-[#FCFBF9] flex items-center justify-center">
        <div className="text-center">
          <p className="font-serif text-2xl mb-4">Ton panier est vide</p>
          <Link to="/shop" className="btn-primary px-6 py-3 inline-block">
            Découvrir la Collection
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FCFBF9]" data-testid="checkout-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="font-serif text-3xl mb-8">Passer la commande</h1>

        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Order Form (Arabic RTL) */}
            <div className="lg:col-span-2">
              <div
                dir="rtl"
                lang="ar"
                className="bg-white border border-[#E0DFDD] shadow-sm rounded-sm overflow-hidden"
                data-testid="inline-cod-form"
              >
                {/* Header */}
                <div className="px-5 py-4 bg-[#F9F8F6] border-b border-[#E0DFDD] text-center">
                  <h3 className="font-medium text-lg text-[#1A1514]">إملأ الإستمارة للطلب</h3>
                </div>

                <div className="p-5 space-y-4">
                  {/* Honeypot */}
                  <input
                    type="text"
                    name="website"
                    value={formData.website}
                    onChange={e => updateField('website', e.target.value)}
                    className="hidden"
                    tabIndex={-1}
                    autoComplete="off"
                  />

                  {/* Phone + Name */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <div className={cn(
                        "flex items-center gap-2 border bg-white px-3 py-2.5",
                        errors.customer_phone ? "border-red-400" : "border-[#E0DFDD]"
                      )}>
                        <Phone size={16} className="text-[#6B6A68] flex-shrink-0" />
                        <input
                          type="tel"
                          value={formData.customer_phone}
                          onChange={e => updateField('customer_phone', e.target.value)}
                          placeholder="رقم الهاتف"
                          className="flex-1 text-sm focus:outline-none bg-transparent text-right"
                          data-testid="input-phone"
                        />
                      </div>
                      {errors.customer_phone && <p className="text-xs text-red-500 mt-1">{errors.customer_phone}</p>}
                    </div>
                    <div>
                      <div className={cn(
                        "flex items-center gap-2 border bg-white px-3 py-2.5",
                        errors.customer_name ? "border-red-400" : "border-[#E0DFDD]"
                      )}>
                        <User size={16} className="text-[#6B6A68] flex-shrink-0" />
                        <input
                          type="text"
                          value={formData.customer_name}
                          onChange={e => updateField('customer_name', e.target.value)}
                          placeholder="الإسم واللقب"
                          className="flex-1 text-sm focus:outline-none bg-transparent text-right"
                          data-testid="input-name"
                        />
                      </div>
                      {errors.customer_name && <p className="text-xs text-red-500 mt-1">{errors.customer_name}</p>}
                    </div>
                  </div>

                  {/* Email (optional) */}
                  <div>
                    <div className={cn(
                      "flex items-center gap-2 border bg-white px-3 py-2.5",
                      errors.customer_email ? "border-red-400" : "border-[#E0DFDD]"
                    )}>
                      <Mail size={16} className="text-[#6B6A68] flex-shrink-0" />
                      <input
                        type="email"
                        value={formData.customer_email}
                        onChange={e => updateField('customer_email', e.target.value)}
                        placeholder="البريد الإلكتروني (إختياري)"
                        className="flex-1 text-sm focus:outline-none bg-transparent text-right"
                        data-testid="input-email"
                      />
                    </div>
                    {errors.customer_email && <p className="text-xs text-red-500 mt-1">{errors.customer_email}</p>}
                  </div>

                  {/* Wilaya + Commune */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <div className={cn(
                        "flex items-center gap-2 border bg-white px-3 py-2.5",
                        errors.wilaya ? "border-red-400" : "border-[#E0DFDD]"
                      )}>
                        <MapPin size={16} className="text-[#6B6A68] flex-shrink-0" />
                        <select
                          value={formData.wilaya}
                          onChange={e => { updateField('wilaya', e.target.value); updateField('commune', ''); }}
                          className="flex-1 text-sm focus:outline-none bg-transparent text-right"
                          data-testid="select-wilaya"
                        >
                          <option value="">إختر ولايتك</option>
                          {wilayas.map(w => (
                            <option key={w.wilaya_id} value={w.wilaya_id}>
                              {w.wilaya_id} — {w.wilaya_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      {errors.wilaya && <p className="text-xs text-red-500 mt-1">{errors.wilaya}</p>}
                    </div>
                    <div>
                      <div className={cn(
                        "flex items-center gap-2 border bg-white px-3 py-2.5",
                        errors.commune ? "border-red-400" : "border-[#E0DFDD]"
                      )}>
                        <Building2 size={16} className="text-[#6B6A68] flex-shrink-0" />
                        {loadingCommunes ? (
                          <div className="flex items-center gap-2 text-sm text-[#6B6A68]">
                            <Loader2 size={14} className="animate-spin" /> جار التحميل...
                          </div>
                        ) : (
                          <select
                            value={formData.commune}
                            onChange={e => updateField('commune', e.target.value)}
                            className="flex-1 text-sm focus:outline-none bg-transparent text-right"
                            disabled={!formData.wilaya}
                            data-testid="select-commune"
                          >
                            <option value="">إختر البلدية</option>
                            {(formData.delivery_type === 'bureau'
                              ? communes.filter(c => c.has_stop_desk === 1)
                              : communes
                            ).map(c => (
                              <option key={c.nom} value={c.nom}>
                                {c.nom}{c.has_stop_desk ? ' (مكتب)' : ''}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                      {errors.commune && <p className="text-xs text-red-500 mt-1">{errors.commune}</p>}
                    </div>
                  </div>

                  {/* Delivery location */}
                  <div>
                    <div className="text-sm text-[#1A1514] font-medium mb-2">مكان التوصيل</div>
                    <div className="space-y-2">
                      <label
                        className={cn(
                          "flex items-center justify-between gap-2 border px-3 py-2.5 cursor-pointer transition-colors",
                          formData.delivery_type === 'home' ? "border-[#1A1514] bg-[#F9F8F6]" : "border-[#E0DFDD]"
                        )}
                        data-testid="delivery-home"
                      >
                        <span className="text-sm">للمنزل {fees.tarif_domicile > 0 && <span className="text-xs text-[#6B6A68]">({fees.tarif_domicile} دج)</span>}</span>
                        <input
                          type="radio"
                          name="delivery_type"
                          checked={formData.delivery_type === 'home'}
                          onChange={() => updateField('delivery_type', 'home')}
                          className="accent-[#1A1514]"
                        />
                      </label>
                      <label
                        className={cn(
                          "flex items-center justify-between gap-2 border px-3 py-2.5 cursor-pointer transition-colors",
                          formData.delivery_type === 'bureau' ? "border-[#1A1514] bg-[#F9F8F6]" : "border-[#E0DFDD]"
                        )}
                        data-testid="delivery-bureau"
                      >
                        <span className="text-sm">لمكتب التوصيل {fees.tarif_bureau > 0 && <span className="text-xs text-[#6B6A68]">({fees.tarif_bureau} دج)</span>}</span>
                        <input
                          type="radio"
                          name="delivery_type"
                          checked={formData.delivery_type === 'bureau'}
                          onChange={() => {
                            updateField('delivery_type', 'bureau');
                            if (formData.commune && !hasBureauForCommune) updateField('commune', '');
                          }}
                          className="accent-[#1A1514]"
                        />
                      </label>
                    </div>
                    {errors.delivery_type && <p className="text-xs text-red-500 mt-1">{errors.delivery_type}</p>}
                  </div>

                  {/* Address (home only) */}
                  {formData.delivery_type === 'home' && (
                    <div>
                      <textarea
                        value={formData.shipping_address}
                        onChange={e => updateField('shipping_address', e.target.value)}
                        rows={2}
                        placeholder="العنوان الكامل (الشارع، رقم البيت...)"
                        className={cn(
                          "w-full border bg-white px-3 py-2.5 text-sm focus:outline-none resize-none text-right",
                          errors.shipping_address ? "border-red-400" : "border-[#E0DFDD]"
                        )}
                        data-testid="input-address"
                      />
                      {errors.shipping_address && <p className="text-xs text-red-500 mt-1">{errors.shipping_address}</p>}
                    </div>
                  )}

                  {/* Notes (optional) */}
                  <div>
                    <textarea
                      value={formData.notes}
                      onChange={e => updateField('notes', e.target.value)}
                      rows={2}
                      placeholder="ملاحظات إضافية (إختياري)"
                      className="w-full border border-[#E0DFDD] bg-white px-3 py-2.5 text-sm focus:outline-none resize-none text-right"
                      data-testid="input-notes"
                    />
                  </div>

                  {/* Reassurance footer */}
                  <div className="flex items-center justify-center gap-4 pt-2 text-xs text-[#6B6A68]">
                    <div className="flex items-center gap-1">
                      <Banknote size={14} className="text-[#1A1514]" />
                      <span>الدفع عند الإستلام</span>
                    </div>
                    <div className="w-px h-3 bg-[#E0DFDD]" />
                    <div className="flex items-center gap-1">
                      <Truck size={14} className="text-[#D4AF37]" />
                      <span>التوصيل 24-48 ساعة</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div
                dir="rtl"
                lang="ar"
                className="bg-white border border-[#E0DFDD] shadow-sm rounded-sm sticky top-24 overflow-hidden"
                data-testid="order-summary"
              >
                {/* Header */}
                <div className="px-5 py-4 bg-[#F9F8F6] border-b border-[#E0DFDD] text-center">
                  <h3 className="font-medium text-lg text-[#1A1514]">ملخص الطلب</h3>
                </div>

                <div className="p-5 space-y-4">
                  {/* Items */}
                  <div className="space-y-3 max-h-64 overflow-y-auto" data-testid="cart-items">
                    {cart.items.map((item) => (
                      <div key={item.cart_item_id} className="flex gap-3" dir="ltr">
                        <div className="w-14 h-16 bg-[#F5F4F2] flex-shrink-0 overflow-hidden">
                          {item.product?.images?.[0]?.url && (
                            <img
                              src={resolveImageUrl(item.product.images[0].url)}
                              alt={item.product.name}
                              className="w-full h-full object-cover"
                            />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-xs truncate">{item.product?.name}</p>
                          <p className="text-xs text-[#6B6A68] mt-0.5">
                            {item.size}{item.color ? ` · ${item.color}` : ''} × {item.quantity}
                          </p>
                          <p className="text-sm mt-1">{formatPrice(item.item_total)}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Promo code */}
                  <div className="pt-3 border-t border-dashed border-[#E0DFDD]">
                    {appliedPromo ? (
                      <div className="flex items-center justify-between bg-[#F0F7F0] border border-green-200 px-3 py-2">
                        <button
                          type="button"
                          onClick={handleRemovePromo}
                          className="p-1 hover:bg-red-50 rounded"
                          data-testid="remove-promo-btn"
                        >
                          <X size={14} className="text-red-500" />
                        </button>
                        <div className="flex items-center gap-2" dir="ltr">
                          <span className="text-xs text-green-600">
                            {appliedPromo.discount_amount > 0 ? `-${formatPrice(appliedPromo.discount_amount)}` : 'Non applicable'}
                          </span>
                          <span className="font-mono text-sm font-medium text-green-700">{appliedPromo.code}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleApplyPromo}
                          disabled={promoLoading || !promoCode.trim()}
                          className="px-4 py-2 bg-[#1A1514] text-white text-xs font-medium disabled:opacity-40"
                          data-testid="apply-promo-btn"
                        >
                          {promoLoading ? <Loader2 size={14} className="animate-spin" /> : 'تطبيق'}
                        </button>
                        <input
                          type="text"
                          value={promoCode}
                          onChange={e => setPromoCode(e.target.value.toUpperCase())}
                          placeholder="كود الخصم (إختياري)"
                          className="flex-1 border border-[#E0DFDD] px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#1A1514] text-right"
                          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleApplyPromo())}
                          data-testid="promo-code-input"
                        />
                        <Tag size={16} className="self-center text-[#6B6A68] flex-shrink-0" />
                      </div>
                    )}
                  </div>

                  {/* Loyalty points */}
                  {loyaltyData && loyaltyData.account?.points_balance >= 100 && (
                    <div className="pt-3 border-t border-dashed border-[#E0DFDD]">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={usePoints}
                          onChange={(e) => {
                            setUsePoints(e.target.checked);
                            if (e.target.checked) {
                              setPointsToUse(Math.min(loyaltyData.account.points_balance, Math.floor(subtotal / (loyaltyData.points_value_da || 10))));
                            } else {
                              setPointsToUse(0);
                            }
                          }}
                          className="w-4 h-4 accent-[#D4AF37]"
                          data-testid="use-points-checkbox"
                        />
                        <span className="text-sm flex items-center gap-2">
                          <Gift size={14} className="text-[#D4AF37]" />
                          إستعمل نقاطي ({loyaltyData.account.points_balance} نقطة)
                        </span>
                      </label>
                      {usePoints && (
                        <div className="mt-2 mr-6">
                          <input
                            type="range"
                            min="100"
                            max={Math.min(loyaltyData.account.points_balance, Math.floor(subtotal / (loyaltyData.points_value_da || 10)))}
                            step="50"
                            value={pointsToUse}
                            onChange={(e) => setPointsToUse(parseInt(e.target.value, 10))}
                            className="w-full accent-[#D4AF37]"
                            dir="ltr"
                          />
                          <p className="text-xs text-[#6B6A68]">
                            {pointsToUse} pts = -{formatPrice(pointsToUse * (loyaltyData.points_value_da || 10))}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Summary lines */}
                  <div className="border-t border-[#E0DFDD] pt-3 space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span>
                        {shippingReady
                          ? (finalShipping === 0
                              ? <span className="text-[#D4AF37] font-semibold">مجاني</span>
                              : <span>{formatPrice(finalShipping)}</span>)
                          : <span className="text-xs text-[#6B6A68] bg-[#F9F8F6] px-2 py-0.5">اختر الولاية ومكان التوصيل</span>}
                      </span>
                      <span className="text-[#1A1514]">سعر التوصيل</span>
                    </div>
                    {!isFreeShipping && subtotal > 0 && freeShippingEnabled && subtotal < freeShippingThreshold && (
                      <p className="text-xs text-[#D4AF37] text-left" dir="ltr">
                        Plus que {formatPrice(freeShippingThreshold - subtotal)} pour la livraison gratuite!
                      </p>
                    )}
                    <div className="flex justify-between items-center">
                      <span>{formatPrice(subtotal)}</span>
                      <span className="text-[#1A1514]">سعر المنتجات</span>
                    </div>
                    {totalDiscount > 0 && (
                      <div className="flex justify-between items-center text-green-600">
                        <span>- {formatPrice(totalDiscount)}</span>
                        <span>الخصم</span>
                      </div>
                    )}

                    {/* Affiliate exclusion info */}
                    {appliedPromo?.has_excluded_items && (
                      <div className="bg-[#F5F4F2] border border-[#E0DFDD] px-3 py-2" data-testid="promo-exclusion-info">
                        <p className="text-xs text-[#6B6A68] leading-relaxed text-right">
                          الكود <span className="font-medium text-[#1A1514]">{appliedPromo.code}</span> تم تطبيقه على المنتجات المؤهلة فقط. المنتجات في عروض ترقية مستثناة من برنامج الإحالة.
                        </p>
                      </div>
                    )}

                    <div className="flex justify-between items-center pt-2 border-t border-[#E0DFDD]">
                      <span className="text-green-600 font-bold text-base" data-testid="total-amount">{formatPrice(total)}</span>
                      <span className="text-[#1A1514] font-semibold">التكلفة الإجمالية</span>
                    </div>
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#1A1514] text-white py-3.5 text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-[#2a2524] transition-colors disabled:opacity-50"
                    data-testid="submit-order-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        <span>جار المعالجة...</span>
                      </>
                    ) : (
                      'أُطلب الآن'
                    )}
                  </button>

                  <p className="text-xs text-[#6B6A68] text-center" dir="ltr">
                    En confirmant, tu acceptes nos <Link to="/cgv" className="underline">CGV</Link>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
